import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { postApi, putApi } from 'apis';
import Buttons from 'components/Global/Buttons';
import FormulaInput from 'components/Global/FormulaField';
import InputField from 'components/Global/InputField';
import InputNumberField from 'components/Global/InputNumberField';
import { earningsFormulaMapping } from 'constants/Data';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	allowedVariablesForEarningsFormula,
	hasFormError,
	toastText,
	validateFormData,
	validateFormula,
} from 'utils/utils';
import { fetchCompanyWizard } from '../../../../../redux/actions/payrollSettingWizardAction';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';

const AddEarningBody: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const { closeDrawerByAnimation, fetchEarnings, editSelected } = props;
	const dispatch = useDispatch<AppDispatch>();

	const [earningsDetails, setEarningsDetails] = useState<any>({
		displayName: '',
		amount: null,
		formula: '',
	});

	const { baseCurrencySymbol } = useSelector(
		(state: any) => state.userProfile.data
	);

	const [formError, setFormError] = useState<any>({
		displayName: false,
		formula: false,
		mutualExclusion: null,
	});

	useEffect(() => {
		if (editSelected) {
			setEarningsDetails({ ...editSelected });
		}
	}, [editSelected]);

	const handleSubmit = async () => {
		let checkFormError: any = validateFormData(
			{ ...earningsDetails },
			{ ...formError }
		);

		const isFormulaValid =
			earningsDetails?.formula &&
			validateFormula(
				earningsDetails.formula as string,
				allowedVariablesForEarningsFormula
			);

		checkFormError = {
			...checkFormError,
			formula: earningsDetails?.formula ? !isFormulaValid : false,
		};

		const isAmountValid =
			earningsDetails.amount !== null &&
			Number(earningsDetails.amount) !== 0;

		// 🔑 enforce mutual exclusion here as well
		if (isAmountValid && isFormulaValid) {
			checkFormError = {
				...checkFormError,
				mutualExclusion:
					'Only one of amount or formula can be entered.',
			};
		} else if (!isAmountValid && !isFormulaValid) {
			checkFormError = {
				...checkFormError,
				mutualExclusion: 'Amount or formula is required.',
			};
		}

		setFormError(checkFormError);

		if (hasFormError(checkFormError)) return;

		const basePayload = {
			...earningsDetails,
			amount: earningsDetails.formula
				? null
				: Number(earningsDetails.amount),
			percent: Number(earningsDetails.percent),
			isExtraEarning: false,
		};

		if (editSelected) {
			const data = {
				earningCodeId: editSelected?.id,
				...basePayload,
				displayName: earningsDetails.displayName,
				formula: earningsDetails?.formula ?? null,
				function: earningsDetails?.function ?? null,
			};
			await updateEarning(data);
		} else {
			await addEarning(basePayload);
			dispatch(fetchCompanyWizard());
		}
	};

	const updateEarning = async (earningData: any) => {
		try {
			setLoading(true);
			const response = await putApi('/earning', earningData);
			fetchEarnings();
			closeDrawerByAnimation();
			let message =
				response?.data?.message || 'Earning updated successfully';
			toastText(message, 'success');
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in updating earning';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	};

	const addEarning = async (finalData: any) => {
		try {
			setLoading(true);
			const response = await postApi('/earning', finalData);
			fetchEarnings();
			closeDrawerByAnimation();
			let message =
				response?.data?.message || 'Earning details saved successfully';
			dispatch(fetchCompanyWizard());
			toastText(message, 'success');
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong when creating earning';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (
		value: string | number | null,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		OnChange(value, name);
	};

	const OnChange = (value: string | number | null, key: string) => {
		setEarningsDetails((prev: any) => ({
			...prev,
			[key]: value,
		}));

		const updatedAmount = key === 'amount' ? value : earningsDetails.amount;
		const updatedFormula =
			key === 'formula' ? value : earningsDetails.formula;

		const isAmountValid =
			updatedAmount !== null && Number(updatedAmount) !== 0;
		const isFormulaValid =
			typeof updatedFormula === 'string' &&
			updatedFormula.trim() !== '' &&
			validateFormula(updatedFormula, allowedVariablesForEarningsFormula);

		let mutualExclusionError = null;

		if (isAmountValid && isFormulaValid) {
			mutualExclusionError =
				'Only one of amount or formula can be entered.';
		} else if (!isAmountValid && !isFormulaValid) {
			mutualExclusionError = 'Amount or formula is required.';
		}

		const updatedFormError = {
			...formError,
			mutualExclusion: mutualExclusionError,
		};

		const checkFormError = validateFormData(
			{ [key]: value },
			updatedFormError
		);
		setFormError(checkFormError);
	};

	const buttons = [
		{
			text: `Cancel`,
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: loading,
			onclick: () => closeDrawerByAnimation(),
		},
		{
			text: `Save`,
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: false,
			onclick: () => handleSubmit(),
		},
	];

	return (
		<>
			<p className={styles['form-container-head-warning']}>
				<b>
					<sup>*</sup>
				</b>{' '}
				Indicated mandatory fields
			</p>
			<div className={styles['side-drawer-body']}>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputField
							name="displayName"
							value={earningsDetails.displayName}
							label="Display Name"
							disabled={editSelected}
							required={true}
							helperText="Please enter Display Name"
							onChange={(value) => {
								handleChange(value, 'displayName', true);
							}}
							isError={formError.displayName}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputNumberField
							name="amount"
							value={earningsDetails.amount}
							label="Amount"
							suffix={baseCurrencySymbol}
							required={false}
							helperText={
								formError.mutualExclusion ||
								(formError.amount ? 'Amount is required' : '')
							}
							onChange={(value) =>
								handleChange(value, 'amount', false)
							}
							isError={!!formError.mutualExclusion}
							disabled={!!earningsDetails.formula}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<FormulaInput
							name="formula"
							value={earningsDetails.formula}
							placeholder={'SAL*OTH*1.5'}
							label="Formula"
							required={false}
							helperText={
								formError.mutualExclusion ||
								(formError.formula
									? 'Enter a valid formula'
									: '')
							}
							onChange={(value) =>
								handleChange(value, 'formula', false)
							}
							isError={!!formError.mutualExclusion}
							disabled={!!earningsDetails.amount}
							labelSuffix={<InfoCircleOutlined />}
							labelSuffixContent={
								<div>
									<p
										style={{
											fontWeight: 500,
											fontSize: '18px',
										}}
									>
										Formula Variables
									</p>
									<ul>
										{Object.entries(
											earningsFormulaMapping
										).map(([key, value]) => (
											<ul key={key}>
												<strong>{key}:</strong> {value}
											</ul>
										))}
									</ul>
								</div>
							}
						/>
					</Col>
				</Row>
				<div className={styles['buttons']}>
					<Buttons buttons={buttons} />
				</div>
			</div>
		</>
	);
};

export default AddEarningBody;
